import { BarChart3, Bot, Brain, LogOut, MessageSquare, Search, Settings, Shield, Sparkles, Upload, Zap } from 'lucide-react'
import { Suspense, useEffect, useRef, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useWindowManager } from './WindowManager/WindowManagerContext'
import { WINDOW_REGISTRY } from './WindowManager/windowRegistry'

interface Command {
  id: string
  label: string
  desc: string
  icon: React.ReactNode
  action: () => void
  category: 'window' | 'action' | 'nav'
}

interface CommandPaletteProps {
  isOpen: boolean
  onClose: () => void
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const { openWindow, windows } = useWindowManager()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'

  const openWindowByRegistry = (registryId: string, title: string, width: number, height: number) => {
    const Component = WINDOW_REGISTRY[registryId]
    if (!Component) return

    const vw = window.innerWidth
    const vh = window.innerHeight
    const openCount = windows.length
    const x = Math.max(40, (vw - width) / 2 + openCount * 24)
    const y = Math.max(40, (vh - height) / 2 + openCount * 24)

    const WrappedComponent = () => (
      <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>Loading...</div>}>
        <Component />
      </Suspense>
    )

    openWindow({ id: registryId, title, component: WrappedComponent, position: { x, y }, size: { width, height } })
  }
  const [search, setSearch] = useState('')
  const [selectedIdx, setSelectedIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const commands: Command[] = [
    { id: 'flzhub', label: 'Open FlzHub', desc: 'Family chat — agents communicate', icon: <MessageSquare size={14} />, action: () => { openWindowByRegistry('flzhub', 'FlzHub', 1100, 700); onClose() }, category: 'window' },
    { id: 'social-window', label: 'Open Social', desc: 'AI social feed', icon: <Bot size={14} />, action: () => { openWindowByRegistry('social-window', 'Social', 1000, 700); onClose() }, category: 'window' },
    { id: 'research-window', label: 'Open Research', desc: 'Research projects', icon: <Brain size={14} />, action: () => { openWindowByRegistry('research-window', 'Research', 900, 650); onClose() }, category: 'window' },
    { id: 'work-window', label: 'Open Work', desc: 'Tasks and freelance', icon: <Zap size={14} />, action: () => { openWindowByRegistry('work-window', 'Work', 900, 650); onClose() }, category: 'window' },
    { id: 'agents-window', label: 'Open Agents', desc: 'Manage AI agents', icon: <Bot size={14} />, action: () => { openWindowByRegistry('agents-window', 'Agents', 900, 650); onClose() }, category: 'window' },
    { id: 'ask-ai', label: 'Ask AI', desc: 'Chat with Agent #522', icon: <Sparkles size={14} />, action: () => { openWindowByRegistry('ask-ai', 'Ask AI', 700, 500); onClose() }, category: 'window' },
    { id: 'store', label: 'Open Store', desc: 'Skills, Extensions, Widgets', icon: <Zap size={14} />, action: () => { openWindowByRegistry('store', 'RunAI Store', 1000, 680); onClose() }, category: 'window' },
    { id: 'twin', label: 'Digital Twin', desc: 'Your AI companion', icon: <Brain size={14} />, action: () => { openWindowByRegistry('twin', 'Digital Twin', 800, 600); onClose() }, category: 'window' },
    { id: 'training', label: 'Training Pipeline', desc: 'LoRA fine-tuning', icon: <Brain size={14} />, action: () => { openWindowByRegistry('training', 'Training', 800, 550); onClose() }, category: 'window' },
    { id: 'neural-brain', label: 'Neural Brain', desc: '3D knowledge graph', icon: <Sparkles size={14} />, action: () => { openWindowByRegistry('neural-brain', 'Neural Brain', 800, 600); onClose() }, category: 'window' },
    { id: 'platform-providers', label: 'Cloud Providers', desc: 'CF, AWS, Azure, GCP', icon: <Upload size={14} />, action: () => { openWindowByRegistry('platform-providers', 'Platforms', 900, 600); onClose() }, category: 'window' },
    { id: 'wallet-mcp', label: 'Wallet', desc: 'FLZ balance and transactions', icon: <BarChart3 size={14} />, action: () => { openWindowByRegistry('wallet-mcp', 'Wallet', 700, 500); onClose() }, category: 'window' },
    { id: 'os-settings', label: 'Settings', desc: 'Profile, security, appearance', icon: <Settings size={14} />, action: () => { openWindowByRegistry('os-settings', 'Settings', 640, 480); onClose() }, category: 'window' },
    ...(isAdmin ? [{ id: 'admin-panel', label: 'Admin Panel', desc: 'Users, agents, tickets, costs', icon: <Shield size={14} />, action: () => { openWindowByRegistry('admin-panel', 'Admin', 1100, 700); onClose() }, category: 'window' as const }] : []),
    { id: 'logout', label: 'Logout', desc: 'Sign out of RunAI', icon: <LogOut size={14} />, action: () => { localStorage.removeItem('token'); window.location.href = '/login' }, category: 'action' },
  ]

  const filtered = search.trim() === ''
    ? commands
    : commands.filter(c => c.label.toLowerCase().includes(search.toLowerCase()) || c.desc.toLowerCase().includes(search.toLowerCase()))

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50)
      setSearch('')
      setSelectedIdx(0)
    }
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIdx(prev => Math.min(prev + 1, filtered.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIdx(prev => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (filtered[selectedIdx]) filtered[selectedIdx].action()
      }
    }
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, filtered, selectedIdx, onClose])

  if (!isOpen) return null

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--os-glass-overlay)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '20vh', zIndex: 9999 }} onClick={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', background: 'var(--os-surface)', backdropFilter: 'blur(20px)', border: '1px solid var(--os-border)', borderRadius: 12, boxShadow: 'var(--os-card-shadow)', width: '90%', maxWidth: 500, maxHeight: '60vh', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--os-divider)' }}>
          <Search size={16} color="var(--os-muted)" />
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setSelectedIdx(0) }}
            placeholder="Search commands... (↑↓ navigate, Enter select, Esc close)"
            style={{ flex: 1, border: 'none', fontSize: 13, fontWeight: 500, color: 'var(--os-text)', outline: 'none', background: 'transparent', fontFamily: 'inherit' }}
          />
          <span style={{ fontSize: 11, color: 'var(--os-muted)', fontFamily: 'monospace', fontWeight: 600 }}>⌘K</span>
        </div>

        {/* Commands */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'var(--os-muted)', fontSize: 13 }}>
              No commands found
            </div>
          ) : (
            filtered.map((cmd, idx) => (
              <button
                key={cmd.id}
                onClick={() => cmd.action()}
                onMouseEnter={() => setSelectedIdx(idx)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 16px',
                  border: 'none',
                  background: idx === selectedIdx ? 'var(--os-hover)' : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.1s',
                  borderBottom: idx < filtered.length - 1 ? '1px solid var(--os-divider)' : 'none',
                  fontFamily: 'inherit',
                }}
              >
                <div style={{ color: 'var(--os-accent)', display: 'flex' }}>
                  {cmd.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--os-text)' }}>{cmd.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--os-muted)', marginTop: 2 }}>{cmd.desc}</div>
                </div>
                <span style={{ fontSize: 10, color: 'var(--os-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {cmd.category === 'window' ? 'Window' : cmd.category === 'nav' ? 'Nav' : 'Action'}
                </span>
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '8px 12px', borderTop: '1px solid var(--os-divider)', background: 'var(--os-bg-secondary)', fontSize: 10, color: 'var(--os-muted)', textAlign: 'center' }}>
          {filtered.length > 0 && <span>{filtered.length} commands found</span>}
        </div>
      </div>
    </div>
  )
}
